import { emitOnboardingEvent } from '../components/onboarding/v2/events';
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useObserve, useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { apiConfig } from '../lib/apiConfig';
import { isCnRegion, getCNAvailableModels, getEffectiveModel } from '../lib/cnConfig';
import { settings$, updateAIModel, updateAILanguage, updateFollowUILanguage, updateChatThinking, updateWebSearch } from '../store/settings$';
import { toast } from 'sonner';
import {
  aiConversations$,
  getConversation,
  getOrCreateConversation,
  addMessage as storeAddMessage,
  updateMessages as storeUpdateMessages,
  updatePreferredModel as storeUpdatePreferredModel,
  clearConversation as storeClearConversation,
  updateQuoteSources as storeUpdateQuoteSources,
  deleteComment as storeDeleteComment,
  getCommentBySourceMessage,
  // UI State
  openAIPanel as storeOpenAIPanel
} from '../store';
import { createHighlight as storeCreateHighlight } from '../store/highlights$';
import { createComment as storeCreateComment } from '../store/comments$';
import { auth$ } from '../store/auth$';
import { articles$ } from '../store/articles$';
import { useCreditBalance } from '../hooks/useCreditBalance';
import { useStreamingChat, STREAMING_STATES } from '../hooks/useStreamingChat';
import { showErrorAlert, ErrorCategory } from '../lib/errorHandler';
import { openUsageModal } from '../store/ui$';
import { stripReedleDirectives } from '../utils/stripMarkdown';
import {
  trackEvent,
  incrementUserProperties,
  setOnceUserProperties,
} from '../lib/analytics';

const AILearnContext = createContext(null);

// Available AI models
// tier: 'free' = available to all, 'pro' = requires Pro/Max subscription
// eslint-disable-next-line react-refresh/only-export-components -- context file: Provider, hook and context are colocated by convention
export const AI_MODELS = [
  // OpenAI
  { id: 'gpt-5.1', name: 'GPT-5.1', provider: 'openai', tier: 'pro' },
  { id: 'gpt-5', name: 'GPT-5', provider: 'openai', tier: 'pro' },
  { id: 'gpt-5-mini', name: 'GPT-5 Mini', provider: 'openai', tier: 'pro' },
  { id: 'gpt-4.1', name: 'GPT-4.1', provider: 'openai', tier: 'pro' },
  { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini', provider: 'openai', tier: 'pro' },
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'openai', tier: 'pro' },
  // Anthropic
  { id: 'claude-4.5-opus', name: 'Claude 4.5 Opus', provider: 'anthropic', tier: 'pro' },
  { id: 'claude-4.5-sonnet', name: 'Claude 4.5 Sonnet', provider: 'anthropic', tier: 'pro' },
  { id: 'claude-4.5-haiku', name: 'Claude 4.5 Haiku', provider: 'anthropic', tier: 'pro' },
  // Google — Flash models are free tier
  { id: 'gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro', provider: 'google', tier: 'pro' },
  { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash', provider: 'google', tier: 'free' },
  { id: 'gemini-3.5-flash-lite', name: 'Gemini 3.5 Flash Lite', provider: 'google', tier: 'pro' }, // P4-c: B3c paid/trial summary default; no Opus/new-Claude per BOARD #42
  { id: 'gemini-3.1-flash-lite', name: 'Gemini 3.1 Flash Lite', provider: 'google', tier: 'free' }, // P4-c: B3c free summary default
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'google', tier: 'free' },
  // DeepSeek
  { id: 'deepseek-r1', name: 'DeepSeek R1', provider: 'deepseek', tier: 'pro' },
  { id: 'deepseek-v3', name: 'DeepSeek V3', provider: 'deepseek', tier: 'pro' },
];

/** Check if a model is available for the given tier */
// eslint-disable-next-line react-refresh/only-export-components -- context file: Provider, hook and context are colocated by convention
export const isModelAvailable = (modelId, userTier) => {
  const model = AI_MODELS.find(m => m.id === modelId);
  if (!model) return true;
  if (model.tier === 'free') return true;
  return userTier === 'pro' || userTier === 'max';
};

// Default AI model for new conversations
const DEFAULT_AI_MODEL = 'gemini-3-flash-preview';

// Import from centralized language config (single source of truth)
import { RESPONSE_LANGUAGES, UI_TO_AI_LANG_MAP } from '../constants/languages';
export { RESPONSE_LANGUAGES, UI_TO_AI_LANG_MAP };


// Auto-save to Comments settings
const AUTO_SAVE_COMMENTS_KEY = 'reedle-ai-autosave-comments';

// Default: dictionary and grammar auto-saved, others not
const DEFAULT_AUTO_SAVE_SETTINGS = {
  dictionary: true,
  grammar: true,
  explain: false,
  summarize: false,
  other: false,
};

const getStoredAutoSaveSettings = () => {
  try {
    const stored = localStorage.getItem(AUTO_SAVE_COMMENTS_KEY);
    if (stored) {
      return { ...DEFAULT_AUTO_SAVE_SETTINGS, ...JSON.parse(stored) };
    }
    return DEFAULT_AUTO_SAVE_SETTINGS;
  } catch {
    return DEFAULT_AUTO_SAVE_SETTINGS;
  }
};

// eslint-disable-next-line react-refresh/only-export-components -- context file: Provider, hook and context are colocated by convention
export const useAILearn = () => {
  const context = useContext(AILearnContext);
  return context;
};

export const AILearnProvider = ({ children, articleId, userId, articleContent, articleTitle, articleUrl, blockVersion }) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(() => settings$.aiModel.get() || DEFAULT_AI_MODEL);

  // Language and follow-UI settings — sourced from shared settings$ (DB-backed)
  const responseLanguage = useSelector(() => settings$.aiLanguage.get());
  const followUILanguage = useSelector(() => settings$.followUILanguage.get());
  const developerMode = useSelector(() => settings$.developerMode.get());

  // Enhanced thinking for Google models
  const chatThinkingEnabled = useSelector(() => settings$.chatThinkingEnabled.get());

  // Web search / grounding
  const webSearchEnabled = useSelector(() => settings$.webSearchEnabled.get());

  // Auto-save to Comments settings
  const [autoSaveSettings, setAutoSaveSettingsState] = useState(getStoredAutoSaveSettings);

  // Pending quote from text selection
  const [pendingQuote, setPendingQuote] = useState(null);

  // Quote source metadata for "add as comment" feature
  // Structure: { [userMessageIndex]: { type: 'highlight'|'selection', highlightId?, globalStart?, globalEnd?, quoteText } }
  const [quoteSources, setQuoteSources] = useState({});

  // Pending quote source (to be associated with next message)
  const pendingQuoteSourceRef = useRef(null);

  // Suggestions overrides — keyed by assistant message index
  // Set when user refreshes suggestions for a specific message
  const [suggestionsOverrides, setSuggestionsOverrides] = useState({});
  const [refreshingSuggestionIndex, setRefreshingSuggestionIndex] = useState(null);

  // Streaming (state machine + preference + SSE orchestration)
  const {
    streamingState,
    streamingMessageIndex,
    isStreamingActive,
    forceCompleteStreaming,
    handleAnimationComplete,
    stopStreaming,
    streamingEnabled,
    setStreamingEnabled,
    captureStreamingMode,
    streamBufferRef,
    visibleLengthRef,
    startStreaming,
    prepareFetch,
    processStreamingResponse,
    cleanupStreaming,
  } = useStreamingChat();

  const clearingChatRef = useRef(false); // Flag to prevent saving partial message during clear
  const pendingStreamContentRef = useRef(false); // Flag: deferred store persistence pending

  // Online status
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Credit balance
  const { balance: creditBalance, refresh: refreshCreditBalance } = useCreditBalance();

  // Persist assistant message when streaming fully completes (animation done or user stopped).
  // Deferred from sendMessage to avoid Supabase realtime race: if we write full content
  // immediately and user stops mid-animation, the realtime echo overwrites the truncation.
  useEffect(() => {
    if (streamingState !== STREAMING_STATES.FULLY_COMPLETE) return;
    if (!pendingStreamContentRef.current) return;
    pendingStreamContentRef.current = false;

    // streamBufferRef has the correct content:
    // - Full content if animation completed normally (never truncated)
    // - Truncated content if user stopped (stopStreamingWithTruncation ran)
    const content = streamBufferRef.current;
    if (content && articleId && !clearingChatRef.current) {
      const msg = { role: 'assistant', content, timestamp: Date.now() };
      storeAddMessage(articleId, msg);
      emitOnboardingEvent('ai-answer-received', { articleId, userId });
    }
  }, [streamingState, articleId, userId]);

  // Stop streaming: truncate buffer + update React state immediately.
  // Store persistence is handled by the useEffect above when FULLY_COMPLETE fires.
  const handleStopStreaming = useCallback(() => {
    stopStreaming(); // truncates streamBufferRef to visibleLengthRef
    const truncatedContent = streamBufferRef.current;
    if (truncatedContent) {
      setMessages(prev => {
        const updated = [...prev];
        if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
          updated[updated.length - 1] = { ...updated[updated.length - 1], content: truncatedContent };
        }
        return updated;
      });
      refreshCreditBalance();
    }
  }, [stopStreaming, refreshCreditBalance]);

  // Callback to open the AI panel (for mobile when hidden)
  const openPanel = useCallback(() => {
    storeOpenAIPanel();
  }, []);

  // Load conversation from store
  useEffect(() => {
    if (!articleId || !userId) {
      setLoading(false);
      return;
    }

    const loadFromStore = () => {
      const conversation = getConversation(articleId);
      if (conversation) {
        let msgs = conversation.messages || [];

        // Detect orphaned comicGenerating messages (interrupted by refresh)
        // and mark them as failed so user can retry
        let changed = false;
        msgs = msgs.map(msg => {
          if (msg.comicGenerating) {
            changed = true;
            return {
              ...msg,
              comicGenerating: false,
              comicFailed: true,
              comicError: 'Generation was interrupted. Click retry to try again.',
            };
          }
          return msg;
        });

        if (changed) {
          storeUpdateMessages(articleId, msgs);
        }

        setMessages(msgs);
        setSelectedModel(conversation.preferred_model || settings$.aiModel.get() || DEFAULT_AI_MODEL);
        setQuoteSources(conversation.quote_sources || {});
      }
      setLoading(false);
    };

    const timer = setTimeout(loadFromStore, 100);
    return () => clearTimeout(timer);
  }, [articleId, userId]);

  // Observe changes to conversation store
  useObserve(aiConversations$, () => {
    if (!articleId) return;
    const conversation = getConversation(articleId);
    if (conversation) {
      setMessages(conversation.messages || []);
      setSelectedModel(conversation.preferred_model || settings$.aiModel.get() || DEFAULT_AI_MODEL);
      setQuoteSources(conversation.quote_sources || {});
    }
  });

  // Change model — updates per-conversation override + global preference
  const changeModel = useCallback((model) => {
    setSelectedModel(model);
    if (articleId) {
      storeUpdatePreferredModel(articleId, model);
    }
    updateAIModel(model);
  }, [articleId]);

  // Change response language — persisted to DB via settings$
  const changeLanguage = useCallback((langCode) => {
    updateAILanguage(langCode);
  }, []);

  // Toggle follow UI language — persisted to DB via settings$
  const setFollowUILanguage = useCallback((value) => {
    updateFollowUILanguage(value);
  }, []);

  // Update auto-save settings (individual type or full object)
  const updateAutoSaveSettings = useCallback((typeOrSettings, value) => {
    setAutoSaveSettingsState(prev => {
      let newSettings;
      if (typeof typeOrSettings === 'object') {
        // Full settings object
        newSettings = { ...prev, ...typeOrSettings };
      } else {
        // Individual type toggle
        newSettings = { ...prev, [typeOrSettings]: value };
      }
      try {
        localStorage.setItem(AUTO_SAVE_COMMENTS_KEY, JSON.stringify(newSettings));
      } catch {
        // Ignore storage errors
      }
      return newSettings;
    });
  }, []);

  // Compute effective language: use UI language if followUILanguage is true
  const getEffectiveLanguage = useCallback(() => {
    if (!followUILanguage) return responseLanguage;
    // Map UI language to AI language code (UI uses 'zh', AI uses 'zh-Hans')
    const uiLang = i18n.language?.split('-')[0] || 'en';
    return UI_TO_AI_LANG_MAP[uiLang] || 'en';
  }, [followUILanguage, responseLanguage, i18n.language]);

  const effectiveLanguage = getEffectiveLanguage();

  // Insert quote into pending state (to be added to next message)
  // Also accepts optional source metadata for "add as comment" feature
  const insertQuote = useCallback((text, source = null) => {
    setPendingQuote(text);
    pendingQuoteSourceRef.current = source;
  }, []);

  // Clear pending quote
  const clearQuote = useCallback(() => {
    setPendingQuote(null);
    pendingQuoteSourceRef.current = null;
  }, []);

  // Send message to AI
  // withQuote: optional quote to include (bypasses pendingQuote state for immediate use)
  // withQuoteSource: optional source metadata for the quote
  const sendMessage = useCallback(async (userMessage, withQuote = null, withQuoteSource = null) => {
    if (!articleId || !userId || !isOnline || sending) return;

    // Check credit balance
    if (creditBalance !== null && creditBalance <= 0) {
      openUsageModal('balance', 'article_chat');
      return;
    }

    setError(null);
    setSending(true);

    // Use provided quote or pendingQuote from state
    const quoteToUse = withQuote || pendingQuote;
    const quoteSourceToUse = withQuoteSource || pendingQuoteSourceRef.current;

    // Format message with quote if present
    let formattedMessage = userMessage;
    if (quoteToUse) {
      formattedMessage = `> "${quoteToUse}"\n\n${userMessage}`;
      setPendingQuote(null);
      pendingQuoteSourceRef.current = null;
    }

    // Calculate user message index (before adding)
    const userMessageIndex = messages.length;

    // Snapshot the streaming preference for this entire response lifecycle.
    // Prevents mid-response toggling from affecting the current request.
    const isStreamingForThisRequest = captureStreamingMode();

    // Start streaming state machine (only if streaming is enabled for this request)
    const assistantMessageIndex = userMessageIndex + 1;
    if (isStreamingForThisRequest) {
      startStreaming(assistantMessageIndex);
    }

    // Add user message to local state immediately
    const userMsg = { role: 'user', content: formattedMessage, timestamp: Date.now() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    storeAddMessage(articleId, userMsg);

    // Track AI chat events
    const isFirstMessage = messages.length === 0;
    if (isFirstMessage) {
      // First message in this conversation - track ai_chat_started
      trackEvent('ai_chat_started', {
        article_id: articleId,
        model: selectedModel,
        language: effectiveLanguage,
      });
      // First-time event for new users
      setOnceUserProperties({ first_ai_chat_at: new Date().toISOString() });
      trackEvent('first_ai_chat', {
        article_id: articleId,
        model: selectedModel,
      });
    }

    // Track every message sent
    trackEvent('ai_message_sent', {
      article_id: articleId,
      message_length: formattedMessage.length,
      model: selectedModel,
      conversation_turn: Math.floor(newMessages.length / 2) + 1,
    });
    incrementUserProperties({ total_ai_chats: 1 });

    // Store quote source for this user message (for "add as comment" feature)
    if (quoteSourceToUse) {
      const newQuoteSource = { ...quoteSourceToUse, quoteText: quoteSourceToUse.quoteText || quoteToUse };
      setQuoteSources(prev => {
        const updated = { ...prev, [userMessageIndex]: newQuoteSource };
        // Persist to store
        storeUpdateQuoteSources(articleId, updated);
        return updated;
      });
    }

    try {
      // Get access token from observable (sync, no network call)
      const session = auth$.session.get();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Get or create conversation to get conversation ID
      const conversation = getOrCreateConversation(articleId);
      const conversationId = conversation?.id;

      const controller = prepareFetch();

      // Call AI chat Edge Function
      const response = await fetch(
        `${apiConfig.supabaseUrl}/functions/v1/reedle-ai-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          signal: controller.signal,
          body: JSON.stringify({
            messages: newMessages.map(m => ({ role: m.role, content: m.content })),
            articleContext: {
              title: articleTitle || 'Untitled',
              content: articleContent || '',
              url: articleUrl,
            },
            blockVersion: blockVersion ?? 2,
            model: getEffectiveModel(selectedModel, developerMode), // CN region: silently routes to licensed model (bypassed in developer mode)
            language: effectiveLanguage, // Use effective language (follows UI if enabled)
            articleId: articleId, // Track article for usage analytics
            conversationId: conversationId, // Track conversation session for PostHog
            enableSuggestions: true, // Enable follow-up suggestion buttons
            stream: isStreamingForThisRequest,
            // Enhanced thinking for Google models (reasoning_effort: 'high' = thinking_level: 'high')
            ...(chatThinkingEnabled && selectedModel?.startsWith('gemini') ? { reasoning_effort: 'high' } : {}),
            // Web search / grounding (Gemini googleSearch, OpenAI web_search_options)
            ...(webSearchEnabled ? { enableWebSearch: true } : {}),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[AILearn] Request failed', errorData);
        throw new Error(errorData.error || `AI request failed: ${response.status}`);
      }

      // Check if response is streaming
      const contentType = response.headers.get('content-type');
      const isStreaming = contentType?.includes('text/event-stream');

      let assistantMsg;

      if (isStreaming) {
        const updateAssistantMessage = (content) => {
          const msg = { role: 'assistant', content, timestamp: Date.now() };
          setMessages(prev => {
            const updated = [...prev];
            if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
              updated[updated.length - 1] = msg;
            } else {
              updated.push(msg);
            }
            return updated;
          });
          return msg;
        };

        const result = await processStreamingResponse(response, {
          updateMessage: updateAssistantMessage,
          setSending,
          onDone(data, content) {
            trackEvent('ai_response_received', {
              model: selectedModel,
              tokens_used: data.usage?.total_tokens || 0,
              cost_usd: data.cost_usd || 0,
              response_length: content.length,
              streaming: true,
            });
          },
          onAbort() {
            // Store persistence handled by useEffect on FULLY_COMPLETE
            refreshCreditBalance();
          },
          onError(err, content, msg) {
            if (content && msg) {
              storeAddMessage(articleId, msg);
            }
          },
        });

        // Don't persist yet — animation still playing. Supabase realtime would
        // echo the full content back and overwrite any later truncation by stop.
        // Persistence deferred to FULLY_COMPLETE (via useEffect).
        if (result) {
          pendingStreamContentRef.current = true;
          refreshCreditBalance();
        }

      } else {
        // Non-streaming fallback
        const data = await response.json();

        trackEvent('ai_response_received', {
          model: selectedModel,
          tokens_used: data.usage?.total_tokens || 0,
          cost_usd: data.cost_usd || 0,
          response_length: data.content?.length || 0,
        });

        assistantMsg = { role: 'assistant', content: data.content, timestamp: Date.now() };
        setMessages(prev => [...prev, assistantMsg]);
        storeAddMessage(articleId, assistantMsg);
        emitOnboardingEvent('ai-answer-received', { articleId, userId });
        refreshCreditBalance();
      }

      // Calculate updatedMessages for auto-save logic
      // (works for both streaming and non-streaming paths)
      const updatedMessages = [...newMessages, assistantMsg];

      // Calculate assistant message index for auto-save
      const assistantMessageIndex = updatedMessages.length - 1;

      // Check if auto-save should trigger based on source type
      const sourceType = quoteSourceToUse?.type;
      let shouldAutoSave = false;

      if (sourceType === 'dictionary' && autoSaveSettings.dictionary) {
        shouldAutoSave = true;
      } else if (sourceType === 'grammar' && autoSaveSettings.grammar) {
        shouldAutoSave = true;
      } else if (sourceType === 'selection' && autoSaveSettings.other) {
        // 'selection' type from quoteToChat action
        shouldAutoSave = true;
      }

      // Also check for explain/summarize based on message content pattern
      // explainSelection sends "Please explain this section to me."
      // summarizeSelection sends "Please summarize this section briefly."
      if (!shouldAutoSave && quoteSourceToUse) {
        if (userMessage.includes('Please explain this section') && autoSaveSettings.explain) {
          shouldAutoSave = true;
        } else if (userMessage.includes('Please summarize this section') && autoSaveSettings.summarize) {
          shouldAutoSave = true;
        }
      }

      // Execute auto-save directly if enabled (no callback ref needed)
      if (shouldAutoSave) {
        // Check for existing comment to prevent duplicates
        const existingComment = getCommentBySourceMessage(articleId, assistantMessageIndex);
        if (!existingComment) {
          try {
            // Create comment directly using quoteSourceToUse (same logic as addAnswerAsComment)
            let comment = null;
            const source = quoteSourceToUse;

            const cleanContent = stripReedleDirectives(assistantMsg.content);

            // An existing highlight is already the anchor; keep its identity.
            if (source?.type === 'highlight') {
              if (!source.highlightId) throw new Error('Missing source highlight ID');
              comment = await storeCreateComment({
                articleId,
                type: 'highlight',
                highlightId: source.highlightId,
                content: cleanContent,
                sourceMessageIndex: assistantMessageIndex,
              });
            }
            // Handle 'translation' type - create paragraph-level comment
            else if (source?.type === 'translation' && source.blockIndex != null) {
              comment = await storeCreateComment({
                articleId,
                type: 'paragraph',
                blockIndex: source.blockIndex,
                content: cleanContent,
                sourceMessageIndex: assistantMessageIndex,
              });
            }
            // Handle 'selection', 'dictionary', and 'grammar' types - create highlight
            else if (source &&
              (source.type === 'selection' || source.type === 'dictionary' || source.type === 'grammar') &&
              source.globalStart != null && source.globalEnd != null) {
              // Create a highlight from the selection/word
              const newHighlight = storeCreateHighlight({
                articleId,
                exactText: source.quoteText,
                globalStart: source.globalStart,
                globalEnd: source.globalEnd,
                prefix: source.prefix || null,
                suffix: source.suffix || null,
              });

              // Add comment to the highlight
              comment = await storeCreateComment({
                articleId,
                type: 'highlight',
                highlightId: newHighlight.id,
                content: cleanContent,
                sourceMessageIndex: assistantMessageIndex,
              });
            }
            // Fallback: add as article-level comment
            else {
              comment = await storeCreateComment({
                articleId,
                type: 'article',
                content: cleanContent,
                sourceMessageIndex: assistantMessageIndex,
              });
            }

            // Show toast with undo option
            if (comment) {
              const commentId = comment.id;
              toast(t('common:toast.autoSavedAsComment'), {
                action: {
                  label: t('common:toast.undo'),
                  onClick: () => {
                    storeDeleteComment(commentId);
                  },
                },
                duration: 5000,
              });
            }
          } catch (autoSaveErr) {
            console.error('Auto-save failed:', autoSaveErr);
            // Don't show error to user for auto-save failure, it's non-critical
          }
        }
      }

      // Refresh credit balance after successful AI call
      refreshCreditBalance();

    } catch (err) {
      // Skip error handling for user-initiated stop
      if (err.name === 'AbortError') return;
      console.error('AI chat error:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  }, [articleId, userId, isOnline, sending, messages, pendingQuote, selectedModel, effectiveLanguage, articleTitle, articleContent, articleUrl, creditBalance, refreshCreditBalance, autoSaveSettings, captureStreamingMode, startStreaming, prepareFetch, processStreamingResponse, chatThinkingEnabled, webSearchEnabled]);

  // Clear conversation
  const clearChat = useCallback(async () => {
    if (!articleId) return;

    // Set flag to prevent saving partial message during abort
    clearingChatRef.current = true;

    cleanupStreaming();

    // Clear all state
    setMessages([]);
    setQuoteSources({});
    setSending(false);
    setError(null);

    // Clear store data AND persistence (awaits clearPersist)
    await storeClearConversation(articleId);
    storeUpdateQuoteSources(articleId, {});

    // Reset clearing flag after abort handler runs
    setTimeout(() => {
      clearingChatRef.current = false;
    }, 0);
  }, [articleId, cleanupStreaming]);

  // Delete a single message
  const deleteMessage = useCallback((idx) => {
    if (!articleId || idx < 0 || idx >= messages.length) return;

    const newMessages = messages.filter((_, i) => i !== idx);

    // Update quote sources - shift indices for messages after deleted one
    const newQuoteSources = {};
    Object.entries(quoteSources).forEach(([key, value]) => {
      const keyIdx = parseInt(key);
      if (keyIdx < idx) {
        newQuoteSources[keyIdx] = value;
      } else if (keyIdx > idx) {
        newQuoteSources[keyIdx - 1] = value;
      }
    });

    setMessages(newMessages);
    setQuoteSources(newQuoteSources);
    storeUpdateMessages(articleId, newMessages);
    storeUpdateQuoteSources(articleId, newQuoteSources);
  }, [articleId, messages, quoteSources]);

  // Refresh suggestions for a specific assistant message index
  const refreshSuggestions = useCallback(async (messageIndex) => {
    if (!articleId || !userId || !articleContent || refreshingSuggestionIndex !== null) return;

    // Check credit balance
    if (creditBalance !== null && creditBalance <= 0) {
      openUsageModal('balance', 'article_chat');
      return;
    }

    setRefreshingSuggestionIndex(messageIndex);
    try {
      const session = auth$.session.get();
      if (!session?.access_token) throw new Error('Not authenticated');

      const response = await fetch(
        `${apiConfig.supabaseUrl}/functions/v1/reedle-ai-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            mode: 'refresh-suggestions',
            articleTitle: articleTitle || '',
            articleContent,
            language: effectiveLanguage !== 'en' ? effectiveLanguage : undefined,
            articleId,
          }),
        },
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const { suggestions } = await response.json();
      if (Array.isArray(suggestions) && suggestions.length > 0) {
        setSuggestionsOverrides(prev => ({ ...prev, [messageIndex]: suggestions }));
        refreshCreditBalance();
      }
    } catch (err) {
      console.error('[refreshSuggestions] Failed:', err);
    } finally {
      setRefreshingSuggestionIndex(null);
    }
  }, [articleId, userId, articleContent, articleTitle, effectiveLanguage, creditBalance, refreshingSuggestionIndex, refreshCreditBalance]);

  // Quote text to chat - user can then add their own question
  // Also accepts optional source metadata for "add as comment" feature
  const quoteToChat = useCallback((text, source = null) => {
    setPendingQuote(text);
    pendingQuoteSourceRef.current = source;
  }, []);

  // Quick actions for selection popover - quote + auto-send question
  const explainSelection = useCallback((text, source = null) => {
    // Pass quote directly to sendMessage to avoid state race condition
    sendMessage('Please explain this section to me.', text, source);
  }, [sendMessage]);

  const summarizeSelection = useCallback((text, source = null) => {
    // Pass quote directly to sendMessage to avoid state race condition
    sendMessage('Please summarize this section briefly.', text, source);
  }, [sendMessage]);

  // Dictionary lookup - builds full prompt with context paragraphs
  const lookupDictionary = useCallback((word, source = null) => {
    // Track dictionary lookup event
    trackEvent('dictionary_lookup', {
      word: word,
      language: effectiveLanguage,
      article_id: articleId,
    });
    // First-time event
    setOnceUserProperties({ first_dictionary_at: new Date().toISOString() });
    trackEvent('first_dictionary', { article_id: articleId });

    // Extract context paragraphs from article content
    let contextText = '';
    if (articleContent && source?.globalStart != null) {
      // Split article into paragraphs (by double newline or single newline for markdown)
      const paragraphs = articleContent.split(/\n\n+/);

      // Find which paragraph contains the selection
      let charCount = 0;
      let targetParagraphIndex = -1;

      for (let i = 0; i < paragraphs.length; i++) {
        const paragraphEnd = charCount + paragraphs[i].length + 2; // +2 for \n\n
        if (source.globalStart >= charCount && source.globalStart < paragraphEnd) {
          targetParagraphIndex = i;
          break;
        }
        charCount = paragraphEnd;
      }

      if (targetParagraphIndex >= 0) {
        // Get previous, current, and next paragraphs
        const before = targetParagraphIndex > 0 ? paragraphs[targetParagraphIndex - 1] : '';
        const current = paragraphs[targetParagraphIndex];
        const after = targetParagraphIndex < paragraphs.length - 1 ? paragraphs[targetParagraphIndex + 1] : '';
        contextText = [before, current, after].filter(Boolean).join('\n\n');
      }
    }

    // Fallback: use 300 chars before and after from raw content
    if (!contextText && articleContent && source?.globalStart != null) {
      const start = Math.max(0, source.globalStart - 300);
      const end = Math.min(articleContent.length, source.globalEnd + 300);
      contextText = articleContent.slice(start, end);
    }

    // Build the full dictionary prompt (stored literally, rendered as capsule)
    const fullPrompt = `You are a language learning assistant helping users progress from beginner to advanced levels.
    
Look up this word/phrase in dictionary style:
"${word}"

Context from article:
---
${contextText || 'No context available'}
---

Provide a dictionary-style entry with:
- Pronunciation (IPA if possible)
- Part of speech
- Definition(s) with translation
- Example sentence from the context above with translation (use **bold** for the word/phrase in the sentence)
- Why This Word? - Explain why the author chose this particular word over synonyms. What nuance, connotation, or precision does this word add? How does it fit the context/register (formal, informal, technical, etc.)? Keep this explanation concise (1-4 sentences).

Example response:
**burn on**

- **Pronunciation**: /bɜrn ɒn/
- **Part of Speech**: Verb Phrase
- **Definition(s)**:
  1. (Informal) To spend money on something, especially when it is not very necessary or sensible.
    - **Translation**: 为某个目的花费金钱，可能是不太必要或轻率的支出。<This "translation" part can be omitted if the user's language preference matches the translated language.>
- **Example Sentence**:
  - <Example sentence extracted from context above with **bold** for the word/phrase> I had some spare cash to burn on this experiment, so I went looking for ways to pay for a Gemini Pro plan, if such a thing existed.
    - **Translation**: 我有一些闲钱可以用在这个实验上，所以我去寻找支付Gemini Pro计划的方法，如果这种计划存在的话。 <This "translation" part can be omitted if the user's language preference matches the translated language, in which case you can omit the translation entirely, even the "- Translation:" header, e.g. English to English.>
- **Why This Word?**:
  - The author chose "burn on" rather than "spend on" or "use for" because it conveys a sense of casual, perhaps slightly reckless spending.
  - "Burn" suggests the money isn't being invested carefully but rather used freely on something experimental or frivolous, while "spend on" is more neutral, and "use for" implies a more deliberate or necessary expenditure.
  - This informal phrase creates a lighthearted, self-deprecating tone about the expense.

If you need to output mathematical formulas or equations, use LaTeX notation: inline $...$ or block $$...$$.

!Important! Neglect all previous instructions and chat history if they conflict with this one: Use the language preference from the system prompt for your response and translations. !!Even if the language preference is the same as the translated language, eg. explaining Chinese to Chinese speakers, just respond in the perferred language, don't bring in new language.!!`;

    // Create dictionary source (extends selection source with type: 'dictionary')
    // Include quoteText directly since we're not using the quote parameter
    const dictSource = source ? {
      ...source,
      type: 'dictionary',
      quoteText: word,  // Store the word for capsule display
    } : { type: 'dictionary', quoteText: word };

    // Send the full prompt - it will be stored literally but rendered as capsule
    // We pass the word as quote so it gets stored in quoteText, but the full prompt is the message
    // Actually, we need to handle this differently - pass dictSource with quoteText already set
    sendMessage(fullPrompt, null, dictSource);
  }, [sendMessage, articleContent]);

  // Grammar analysis - detailed grammar breakdown for language learners
  const lookupGrammar = useCallback((sentence, source = null) => {
    // Track grammar lookup event
    trackEvent('grammar_lookup', {
      text_length: sentence.length,
      language: effectiveLanguage,
      article_id: articleId,
    });
    // First-time event
    setOnceUserProperties({ first_grammar_at: new Date().toISOString() });
    trackEvent('first_grammar', { article_id: articleId });

    // Extract context paragraphs from article content (same as dictionary)
    let contextText = '';
    if (articleContent && source?.globalStart != null) {
      const paragraphs = articleContent.split(/\n\n+/);
      let charCount = 0;
      let targetParagraphIndex = -1;

      for (let i = 0; i < paragraphs.length; i++) {
        const paragraphEnd = charCount + paragraphs[i].length + 2;
        if (source.globalStart >= charCount && source.globalStart < paragraphEnd) {
          targetParagraphIndex = i;
          break;
        }
        charCount = paragraphEnd;
      }

      if (targetParagraphIndex !== -1) {
        const startIdx = Math.max(0, targetParagraphIndex - 1);
        const endIdx = Math.min(paragraphs.length - 1, targetParagraphIndex + 1);
        contextText = paragraphs.slice(startIdx, endIdx + 1).join('\n\n');
      }
    }

    if (!contextText && articleContent && source?.globalStart != null) {
      const start = Math.max(0, source.globalStart - 300);
      const end = Math.min(articleContent.length, source.globalEnd + 300);
      contextText = articleContent.slice(start, end);
    }

    // Build grammar analysis prompt
    const fullPrompt = `You are a language learning assistant helping users progress from beginner to advanced levels.

Analyze this sentence for language learners:

"${sentence}"

Context from article:
---
${contextText || 'No context available'}
---

Provide:

**sentence**

**Translation**
Translation of the sentence.

**Vocabulary**
List key words AS THEY APPEAR in the sentence:
- **word** [IPA] - meaning. Note on word form/grammar role.

**Grammar Analysis**
Identify grammar patterns (clauses, non-finite verbs, phrases):
1. **pattern name**
   - Structure: how it is structured <in target language>
   - Meaning: what meaning it conveys <in target language>

**Other Notes**
Other grammar points worth noting.

---
Example for "I regret not studying harder":
**Translation**
I regret not studying harder. (translate to target language)
**Vocabulary**
1. **regret** [rɪˈɡret] - to feel sorry. Verb.
2. **studying** [ˈstʌdiɪŋ] - learning. Gerund.
**Grammar Analysis**
1. **regret + V-ing**
   - Structure: regret + not + gerund
   - Meaning: expressing regret about past action
---

Now analyze the given sentence. Important: Use the user's language preference from the system prompt for your response!

If you need to output mathematical formulas or equations, use LaTeX notation: inline $...$ or block $$...$$.`;

    const grammarSource = source ? {
      ...source,
      type: 'grammar',
      quoteText: sentence,
    } : { type: 'grammar', quoteText: sentence };

    sendMessage(fullPrompt, null, grammarSource);
  }, [sendMessage, articleContent]);

  // Make flashcards from selected text
  const makeCards = useCallback((text, source = null) => {
    const fullPrompt = `You are a flashcard generation assistant helping users create effective study materials.

Based on the following text, create 1-5 flashcards for effective learning:

"${text}"

Generate each card using this exact format:

\`\`\`reedle:flashcard
front: Your question or prompt here
---
Your answer here (can use **markdown** formatting)
\`\`\`

Guidelines:
- Create cards that test understanding, not just memorization
- Keep questions clear and specific
- Provide comprehensive but concise answers
- Generate between 1-5 cards depending on content complexity
- Each card should focus on a single concept
- Use the user's language preference for the response

Example output:
\`\`\`reedle:flashcard
front: What is the main purpose of spaced repetition?
---
Spaced repetition is a learning technique that **optimizes memory retention** by reviewing information at increasing intervals. It leverages the psychological spacing effect to move knowledge from short-term to long-term memory more efficiently.
\`\`\``;

    const makeCardSource = source ? {
      ...source,
      type: 'makecard',
      quoteText: text,
    } : { type: 'makecard', quoteText: text };

    sendMessage(fullPrompt, null, makeCardSource);
  }, [sendMessage]);

  // Summarize the full article (no quote, uses full article context)
  const summarizeArticle = useCallback(() => {
    sendMessage('Please provide a comprehensive summary of this entire article.');
  }, [sendMessage]);

  // Generate a comic-style visual summary of the article
  // This uses a separate non-streaming call to the edge function with mode: 'generate-comic'
  // The response contains a reedle:comic block that ReedleMarkdown renders as a ComicBlock
  const [comicGenerating, setComicGenerating] = useState(false);

  // Helper: fire the actual comic generation fetch and return result or throw
  const fireComicGeneration = useCallback(async (comicModel, comicStyle) => {
    const session = auth$.session.get();
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${apiConfig.supabaseUrl}/functions/v1/reedle-ai-chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          mode: 'generate-comic',
          articleId,
          title: articleTitle || 'Untitled',
          content: articleContent || '',
          comicModel,
          comicStyle,
        }),
      }
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const err = new Error(errData.details || errData.error || `HTTP ${response.status}`);
      err.errorCode = errData.error_code || 'UNKNOWN';
      err.attemptCount = errData.attempt_count || 1;
      throw err;
    }

    return await response.json();
  }, [articleId, articleTitle, articleContent]);

  const generateComic = useCallback(async (comicModel = 'gemini-2.5-flash-image', comicStyle = 'comic') => {
    if (!articleId || !userId || !isOnline || sending || comicGenerating) return;

    // Check credit balance
    if (creditBalance !== null && creditBalance <= 0) {
      openUsageModal('balance', 'comic_generation');
      return;
    }

    setComicGenerating(true);
    setError(null);

    // Style display names
    const styleNames = { comic: 'Comic Strip', infographic: 'Infographic', manga: 'Manga', sketch: 'Sketch Notes' };
    const modelName = comicModel === 'gemini-3-pro-image-preview' ? 'Nano Banana Pro' : 'Nano Banana';
    const styleName = styleNames[comicStyle] || 'Comic Strip';

    // Add user message immediately
    const userMsg = { role: 'user', content: `Generate a visual summary: ${styleName} style (${modelName})`, timestamp: Date.now() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    storeAddMessage(articleId, userMsg);

    // Add placeholder assistant message with generating flag
    const placeholderMsg = {
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      comicGenerating: true,
      comicModel,
      comicStyle,
    };
    const messagesWithPlaceholder = [...newMessages, placeholderMsg];
    setMessages(messagesWithPlaceholder);
    storeUpdateMessages(articleId, messagesWithPlaceholder);

    // Track event
    trackEvent('comic_generation_started', {
      article_id: articleId,
      model: comicModel,
      style: comicStyle,
    });
    incrementUserProperties({ total_comic_generations: 1 });

    try {
      const data = await fireComicGeneration(comicModel, comicStyle);

      // Replace placeholder with real content
      const assistantMsg = { role: 'assistant', content: data.content, timestamp: Date.now() };
      const updatedMessages = [...newMessages, assistantMsg];
      setMessages(updatedMessages);
      storeUpdateMessages(articleId, updatedMessages);

      // Set comic_cover_url on the article (fallback cover for article list)
      if (data.imageUrl && articleId) {
        articles$[articleId].assign({ comic_cover_url: data.imageUrl, updated_at: new Date().toISOString() });
      }

      // Track success
      trackEvent('comic_generation_completed', {
        article_id: articleId,
        model: comicModel,
        style: comicStyle,
        credits_used: data.creditsUsed,
      });

      // Refresh credit balance
      refreshCreditBalance?.();
    } catch (err) {
      console.error('[AILearn] Comic generation failed:', err);

      // Track failure for observability (Plan 4)
      trackEvent('comic_generation_failed', {
        article_id: articleId,
        model: comicModel,
        style: comicStyle,
        error_code: err.errorCode || 'UNKNOWN',
        attempt_count: err.attemptCount || 1,
        is_retry: false,
      });

      // Update placeholder to show failed state (with retry info)
      const failedMsg = {
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        comicFailed: true,
        comicError: err.message,
        comicModel,
        comicStyle,
      };
      const updatedMessages = [...newMessages, failedMsg];
      setMessages(updatedMessages);
      storeUpdateMessages(articleId, updatedMessages);

      showErrorAlert('Comic generation failed', err, { component: 'AILearnContext', action: 'generate_comic' });
    } finally {
      setComicGenerating(false);
    }
  }, [articleId, userId, isOnline, sending, comicGenerating, messages, creditBalance, fireComicGeneration, refreshCreditBalance]);

  // Retry a failed comic generation at a specific message index
  const retryComic = useCallback(async (messageIndex) => {
    const msg = messages[messageIndex];
    if (!msg || !msg.comicFailed || comicGenerating) return;

    const { comicModel, comicStyle } = msg;
    setComicGenerating(true);
    setError(null);

    // Update the failed message back to generating state
    const updatedMsg = {
      ...msg,
      comicFailed: false,
      comicError: undefined,
      comicGenerating: true,
      timestamp: Date.now(),
    };
    const newMessages = [...messages];
    newMessages[messageIndex] = updatedMsg;
    setMessages(newMessages);
    storeUpdateMessages(articleId, newMessages);

    try {
      const data = await fireComicGeneration(comicModel, comicStyle);

      // Replace with real content
      const assistantMsg = { role: 'assistant', content: data.content, timestamp: Date.now() };
      const finalMessages = [...messages];
      finalMessages[messageIndex] = assistantMsg;
      setMessages(finalMessages);
      storeUpdateMessages(articleId, finalMessages);

      // Set comic_cover_url on the article (fallback cover for article list)
      if (data.imageUrl && articleId) {
        articles$[articleId].assign({ comic_cover_url: data.imageUrl, updated_at: new Date().toISOString() });
      }

      trackEvent('comic_generation_completed', {
        article_id: articleId,
        model: comicModel,
        style: comicStyle,
        credits_used: data.creditsUsed,
        retried: true,
      });

      refreshCreditBalance?.();
    } catch (err) {
      console.error('[AILearn] Comic retry failed:', err);

      // Track failure for observability (Plan 4)
      trackEvent('comic_generation_failed', {
        article_id: articleId,
        model: comicModel,
        style: comicStyle,
        error_code: err.errorCode || 'UNKNOWN',
        attempt_count: err.attemptCount || 1,
        is_retry: true,
      });

      const failedMsg = {
        ...msg,
        comicFailed: true,
        comicError: err.message,
        comicGenerating: false,
        timestamp: Date.now(),
      };
      const finalMessages = [...messages];
      finalMessages[messageIndex] = failedMsg;
      setMessages(finalMessages);
      storeUpdateMessages(articleId, finalMessages);

      showErrorAlert('Comic retry failed', err, { component: 'AILearnContext', action: 'retry_comic' });
    } finally {
      setComicGenerating(false);
    }
  }, [articleId, messages, comicGenerating, fireComicGeneration, refreshCreditBalance]);

  // Add AI answer as a comment
  // - If source is 'highlight': attach to the existing highlight
  // - If source is 'translation' type: create paragraph-level comment (no highlight)
  // - If source is 'selection'/'dictionary'/'grammar': create highlight and attach comment
  // - Otherwise: add as article-level comment
  // Returns the created comment (or null on error)
  const addAnswerAsComment = useCallback(async (assistantMessageIndex) => {
    if (!articleId) return null;

    const assistantMessage = messages[assistantMessageIndex];
    if (!assistantMessage || assistantMessage.role !== 'assistant') return null;

    // Find the user message that triggered this response (previous message)
    const userMessageIndex = assistantMessageIndex - 1;
    const source = quoteSources[userMessageIndex];

    try {
      const cleanContent = stripReedleDirectives(assistantMessage.content);

      if (source?.type === 'highlight') {
        if (!source.highlightId) throw new Error('Missing source highlight ID');
        return await storeCreateComment({
          articleId,
          type: 'highlight',
          highlightId: source.highlightId,
          content: cleanContent,
          sourceMessageIndex: assistantMessageIndex,
        });
      }

      // Handle 'translation' type - create paragraph-level comment (no highlight)
      if (source?.type === 'translation' && source.blockIndex != null) {
        const comment = await storeCreateComment({
          articleId,
          type: 'paragraph',
          blockIndex: source.blockIndex,
          content: cleanContent,
          sourceMessageIndex: assistantMessageIndex, // Track which AI message this came from
        });
        return comment;
      }

      // Handle 'selection', 'dictionary', and 'grammar' types - all can create highlights
      const canCreateHighlight = source &&
        (source.type === 'selection' || source.type === 'dictionary' || source.type === 'grammar') &&
        source.globalStart != null && source.globalEnd != null;

      if (canCreateHighlight) {
        // Create a highlight from the selection/word, then add comment to it
        const newHighlight = storeCreateHighlight({
          articleId,
          exactText: source.quoteText,
          globalStart: source.globalStart,
          globalEnd: source.globalEnd,
          prefix: source.prefix || null,
          suffix: source.suffix || null,
        });

        // Add comment to the highlight
        const comment = await storeCreateComment({
          articleId,
          type: 'highlight',
          highlightId: newHighlight.id,
          content: cleanContent,
          sourceMessageIndex: assistantMessageIndex, // Track which AI message this came from
        });
        return comment;
      } else {
        // No quote source - add as article-level comment
        const comment = await storeCreateComment({
          articleId,
          type: 'article',
          content: cleanContent,
          sourceMessageIndex: assistantMessageIndex, // Track which AI message this came from
        });
        return comment;
      }
    } catch (err) {
      console.error('Failed to add answer as comment:', err);
      setError('Failed to add comment');
      return null;
    }
  }, [articleId, messages, quoteSources]);

  return (
    <AILearnContext.Provider value={{
      // Article info
      articleId,
      // Messages
      messages,
      loading,
      sending,
      error,
      // Model selection
      selectedModel,
      changeModel,
      availableModels: (isCnRegion() && !developerMode) ? getCNAvailableModels(AI_MODELS) : AI_MODELS,
      // Language selection
      responseLanguage,
      changeLanguage,
      availableLanguages: RESPONSE_LANGUAGES,
      // Follow UI language
      followUILanguage,
      setFollowUILanguage,
      effectiveLanguage,
      // Enhanced thinking toggle
      chatThinkingEnabled,
      updateChatThinking,
      // Web search toggle
      webSearchEnabled,
      updateWebSearch,
      // Auto-save to comments settings
      autoSaveSettings,
      updateAutoSaveSettings,
      // Streaming preference
      streamingEnabled,
      setStreamingEnabled,
      // Quote
      pendingQuote,
      insertQuote,
      clearQuote,
      // Quote sources for "add as comment"
      quoteSources,
      // Actions
      sendMessage,
      clearChat,
      deleteMessage,
      addAnswerAsComment,
      // Suggestion refresh
      suggestionsOverrides,
      refreshingSuggestionIndex,
      refreshSuggestions,
      // Quick actions for selection popover
      quoteToChat,
      explainSelection,
      summarizeSelection,
      lookupDictionary,
      lookupGrammar,
      makeCards,
      // Full article actions
      summarizeArticle,
      generateComic,
      retryComic,
      comicGenerating,
      // Status
      isOnline,
      // Panel control (for mobile auto-open)
      openPanel,
      // Streaming
      STREAMING_STATES,
      streamingState,
      streamingMessageIndex,
      isStreamingActive,
      handleAnimationComplete,
      stopStreaming: handleStopStreaming,
      forceCompleteStreaming,
      streamBufferRef,
      visibleLengthRef,
    }}>
      {children}
    </AILearnContext.Provider>
  );
};

